/**
 * BeforeInstallPrompt Hook — PWA Install Prompt
 * Spec ref: SPEC-025 §3.3 (Install Prompt Hook),
 *           §2.2 (30s delay OR 2nd visit, dismiss 7 days)
 *
 * - Banner appears after 30s on the app OR on 2nd visit
 * - Dismiss persists in localStorage (no show for 7 days)
 * - Install triggers native browser prompt
 * - Post-install: detects standalone display mode
 *
 * P-159 (Sprint 49 — "desinstalé la App y al reinstall dice que ya está
 * instalada"): Chrome en Android puede reportar la PWA como instalada
 * (WebAPK vivo en el app drawer) aunque NO haya ícono en la pantalla de
 * inicio. En ese estado `beforeinstallprompt` NUNCA dispara y el intento
 * de instalación muestra el diálogo nativo "la aplicación ya está
 * instalada" sin instalar nada.
 *
 * P-164 (Sprint 51 — "el boton de instalar App ahora no me aparece en el
 * celular", Founder post-deploy Sprint 49): el fix P-159 reemplazó un
 * defecto por otro — al marcar `isInstalled=true` cuando
 * `getInstalledRelatedApps()` reportaba el WebAPK (vivo o STALE), el
 * banner dejaba de aparecer PARA SIEMPRE en ese dispositivo. El Founder
 * desinstaló la App y NO puede volver a ver el botón de instalación
 * (Chrome reporta un WebAPK stale → banner suprimido + el evento
 * `beforeinstallprompt` nunca dispara porque Chrome "cree" que ya está
 * instalada).
 *
 * Fix de raíz P-164 (estado REAL del navegador, sin supresión ciega):
 *   1. `isInstalled` SOLO con señales REALES de instalación: modo
 *      standalone (la app corriendo instalada) o evento `appinstalled`.
 *      `getInstalledRelatedApps()` YA NO suprime el banner —
 *   2. El estado "Chrome reporta WebAPK pero NO corremos standalone" se
 *      expone como elegible de RECUPERACIÓN: el banner SE MUESTRA y el
 *      click (sin deferredPrompt — el evento nunca disparó) muestra la
 *      guía accionable P-159 ("ábrela desde tu lista de aplicaciones; si
 *      no la encuentras, desinstálala desde Ajustes e instala de nuevo")
 *      — exactamente el camino de recuperación que el Founder necesita.
 *   3. Captura del evento a NIVEL DE MÓDULO (pre-hidratación): Chrome
 *      puede disparar `beforeinstallprompt` antes de que React hidrate y
 *      registre el listener del useEffect → el evento se PERDÍA y el
 *      banner nunca aparecía. El listener singleton a nivel de módulo se
 *      registra durante la evaluación del chunk (antes de la hidratación)
 *      y guarda el evento para la primera instancia del hook.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Guía accionable para el estado "Chrome cree que ya está instalada".
 *
 * P-196 (SPEC-130): distingue 2 casos con UX específica:
 *   - `webapkStale=true`: Chrome reporta WebAPK (stale tras desinstalar)
 *     PERO `beforeinstallprompt` no disparó → el usuario acaba de
 *     desinstalar y Chrome mantiene un registro obsoleto. Toast warning
 *     más largo (15s) con pasos accionables (limpiar caché Chrome,
 *     reiniciar, esperar 24h).
 *   - `webapkStale=false`: app realmente instalada (display-mode standalone
 *     o appinstalled disparó, pero el botón se renderizó porque el flag
 *     cambió después del mount). Toast info estándar P-159 (9s) — "ábrela
 *     desde tu lista de aplicaciones".
 */
function showAlreadyInstalledGuidance(webapkStale: boolean): void {
  if (webapkStale) {
    toast.warning('Chrome cree que Profileia sigue instalada', {
      description:
        'Acabas de desinstalar la app pero Chrome mantiene un registro obsoleto (WebAPK stale). Para reinstalar: 1) Abre Ajustes → Aplicaciones → Chrome → Almacenamiento → Borrar caché. 2) Reinicia Chrome. 3) Vuelve a esta página y haz click en "Instalar App". Si el problema persiste, espera 24h — Chrome sincroniza su estado de WebAPK en segundo plano.',
      duration: 15000,
    });
  } else {
    toast.info('Profileia ya está instalada en este dispositivo', {
      description: 'Ábrela desde tu lista de aplicaciones (o el buscador). Si no la encuentras, desinstálala desde Ajustes → Aplicaciones e instala de nuevo.',
      duration: 9000,
    });
  }
}

// ─── P-164 (3): captura pre-hidratación a nivel de módulo ────────────────
// Chrome dispara `beforeinstallprompt` cuando pasa los checks de
// installability (manifest + SW), que puede ocurrir ANTES de que React
// hidrate y el useEffect del hook registre su listener. En teléfonos
// lentos (hidratación 2-4s) el evento se perdía → isInstallable quedaba
// false → "el botón no aparece". Este listener singleton vive desde la
// evaluación del chunk (pre-hidratación) y guarda el evento.
// 'use client' NO evita que el módulo se evalúe en SSR → guard de window.
let capturedPromptEvent: BeforeInstallPromptEvent | null = null;
let capturedPromptAvailable = false;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    capturedPromptEvent = e as BeforeInstallPromptEvent;
    capturedPromptAvailable = true;
  });
}

/** Lee (y limpia) el evento capturado pre-hidratación. */
function consumeCapturedPrompt(): BeforeInstallPromptEvent | null {
  const ev = capturedPromptEvent;
  capturedPromptEvent = null;
  return ev;
}

function wasPromptCaptured(): boolean {
  return capturedPromptAvailable;
}

export function useInstallPrompt() {
  // P-164: inicializar DESDE el stash del módulo — si el evento disparó
  // pre-hidratación, ya está aquí; no dependemos del timing del useEffect.
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    () => (typeof window !== 'undefined' ? consumeCapturedPrompt() : null),
  );
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  // P-164 (2): "Chrome reporta WebAPK instalado pero NO corremos
  // standalone" — el banner se muestra para RECUPERACIÓN (el click da la
  // guía P-159). Este flag NO es isInstalled (eso sería la supresión que
  // causó el bug del botón invisible).
  const [chromeReportsInstalled, setChromeReportsInstalled] = useState(false);

  // P-196 (SPEC-130): WebAPK stale — Chrome reporta WebAPK instalado
  // (getInstalledRelatedApps() retorna apps) PERO `beforeinstallprompt`
  // no disparó tras 2s. Es el caso donde el usuario desinstaló la app y
  // Chrome mantiene un registro obsoleto. La guía accionable debe ser
  // específica (toast.warning con pasos + modal StaleWebApkDialog).
  const [webapkStale, setWebapkStale] = useState(false);

  // P-196 (SPEC-130): ref para evitar stale closure del `deferredPrompt`
  // dentro del `setTimeout(2000)` que detecta el WebAPK stale. El state
  // `deferredPrompt` capturado en el closure del setTimeout tiene el valor
  // al momento de agendar el timer (típicamente `null` porque el evento
  // aún no disparó) → no podemos leer el estado fresco. El ref se
  // sincroniza con el state en cada render vía el useEffect debajo.
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  useEffect(() => {
    deferredPromptRef.current = deferredPrompt;
  }, [deferredPrompt]);

  useEffect(() => {
    // Bug #36 fix (R3 CORROBORACIÓN QA): SSR guards. window and localStorage
    // are NOT available during Server-Side Rendering (Next.js Edge/Node runtime).
    // Without these guards, the page crashes on SSR (window is not defined).
    // The 'use client' directive at the top of this file makes this hook
    // client-only at the bundle level, BUT Next.js still runs the module
    // during SSR for static prerendering — the useEffect body is NOT executed
    // during SSR (useEffect is client-only), but window access at the top
    // level of useEffect IS evaluated. The check `typeof window !== 'undefined'`
    // is the standard pattern to guard against this.
    if (typeof window === 'undefined') return;

    // P-164: el evento pudo haber disparado pre-hidratación (capturado en
    // el stash del módulo) — sincronizar el FLAG de installability antes
    // de cualquier guard que pudiera retornar temprano (el stash se
    // consumió en el initializer del useState; aquí solo refleja que el
    // navegador SÍ es instalable).
    if (wasPromptCaptured()) {
      setIsInstallable(true);
    }

    // Check if already installed (running in standalone mode) — señal REAL
    // (la app corriendo instalada) → esta SÍ justifica ocultar el banner.
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // P-159 → P-164 (2): consultar el estado de instalación reportado por
    // Chrome (WebAPK) vía getInstalledRelatedApps. Chrome Android lo
    // soporta para apps del MISMO origen; en los demás navegadores la API
    // no existe → guard opcional → seguimos con el flujo clásico.
    // ⚠ P-164: este estado YA NO se traduce en isInstalled (la supresión
    // ciega P-159 hizo desaparecer el botón para siempre en dispositivos
    // con WebAPK stale). Se registra como elegible de RECUPERACIÓN: el
    // banner sigue visible y el click sin deferredPrompt muestra la guía.
    const relatedApps = (navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<Array<{ id?: string; platform?: string }>>;
    }).getInstalledRelatedApps;
    if (typeof relatedApps === 'function') {
      relatedApps
        .call(navigator)
        .then((apps) => {
          if (apps && apps.length > 0) {
            // P-196 (SPEC-130): distinguir WebAPK stale de app real.
            // Si getInstalledRelatedApps() reporta apps PERO
            // beforeinstallprompt no disparó (isInstallable sigue false
            // tras 2s), es un WebAPK stale (Chrome cree que está
            // instalada pero el usuario la desinstaló). En ese caso,
            // marcamos webapkStale=true para que la guía sea específica
            // (toast.warning + modal con pasos accionables).
            setChromeReportsInstalled(true);
            // Esperar 2s a ver si beforeinstallprompt dispara (latencia
            // normal del event loop). Si después de 2s el flag
            // wasPromptCaptured() sigue false Y deferredPromptRef.current
            // sigue null, es stale.
            //
            // Nota técnica: NO usamos `deferredPrompt` (state) ni
            // `isInstallable` (state) directamente en el closure del
            // setTimeout porque son stale closures (capturan el valor al
            // momento de agendar el timer, no el valor fresco al ejecutar).
            // Usamos `wasPromptCaptured()` (flag de módulo siempre fresco)
            // + `deferredPromptRef.current` (ref siempre fresco).
            setTimeout(() => {
              if (!wasPromptCaptured() && !deferredPromptRef.current) {
                setWebapkStale(true);
              }
            }, 2000);
          }
        })
        .catch(() => {
          // Permisos/API restringida — flujo clásico como fallback.
        });
    }

    // Check if dismissed recently (7-day cooldown)
    // Bug #36 fix: guard localStorage access for SSR
    if (typeof localStorage === 'undefined') return;
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() < parseInt(dismissed, 10)) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    // D-012 fix (SPEC-043 Batch D): extract the inline arrow to a NAMED
    // function so we can removeEventListener('appinstalled', onInstalled)
    // in the cleanup. Pre-fix, the cleanup only removed 'beforeinstallprompt'
    // — 'appinstalled' was registered with an inline arrow and could never
    // be removed → memory leak (the listener held a closure over
    // `setIsInstalled`, keeping the component's state alive after unmount).
    const onInstalled = () => setIsInstalled(true);

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
    // P-164: deps vacías (patrón original) — la sincronización del stash
    // pre-hidratación ocurre en el initializer del useState y en el flag
    // wasPromptCaptured() de arriba; los listeners viven por el ciclo de
    // vida del componente como en el diseño original.
  }, []);

  // P-164 (2): el banner se ofrece cuando HAY capacidad real de instalar
  // (beforeinstallprompt capturado) O cuando Chrome reporta un WebAPK que
  // el usuario no encuentra (recuperación stale). Firefox/Safari desktop:
  // ninguna de las dos → el banner NO aparece (SPEC-066 AUDIT-066-009).
  const offerInstall = isInstallable || chromeReportsInstalled;

  // AUDIT3-FECONF-022 fix (BATCH-4): wrap `install` + `dismiss` in
  // useCallback so their identities are stable across renders.
  // Pre-fix, both were defined as plain `async () => {}` / `() => {}`
  // arrow expressions inside the hook body — React re-creates a new
  // function reference on every render. Consumers that listed these
  // callbacks in useEffect deps (e.g., `useEffect(..., [install])`)
  // re-fired their effects every render → potential infinite loops +
  // redundant side-effects. Now the identity only changes when the
  // underlying state (`deferredPrompt`) changes.
  const install = useCallback(async (): Promise<void> => {
    // P-159: sin deferredPrompt el evento beforeinstallprompt nunca
    // disparó — la causa más común en Android es que Chrome aún
    // considera la PWA instalada (WebAPK sin ícono tras un "Quitar" en
    // vez de "Desinstalar", o estado stale tras un uninstall reciente).
    // El clic "Instalar" NO puede abrir el diálogo nativo en ese estado
    // — P-164 mantiene esta guía accionable (es el camino de
    // recuperación para el estado stale del Founder).
    //
    // P-196 (SPEC-130): pasamos `webapkStale` a la guía para distinguir
    // el toast warning específico (stale) del toast info genérico (app
    // real).
    if (!deferredPrompt) {
      showAlreadyInstalledGuidance(webapkStale);
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      toast.success('¡Profileia instalada!', { description: 'Accede desde tu pantalla de inicio' });
    }
    setDeferredPrompt(null);
  }, [deferredPrompt, webapkStale]);

  const dismiss = useCallback((): void => {
    // P-UX-014 (SPEC-165): extend dismiss window from 7 → 30 days.
    // QA UX Specialist (Sprint 81) reported the banner was too persistent
    // for frequent users — "Ahora no" felt useless because the banner
    // reappeared within a week. 30 days aligns with the Founder directive
    // (SPEC-165 §2 P-UX-014) of "no molestar al usuario frecuente".
    //
    // Bug #36 fix: guard localStorage access (dismiss can be called on first render)
    if (typeof localStorage === 'undefined') return;
    const until = Date.now() + 30 * 24 * 60 * 60 * 1000;
    localStorage.setItem('pwa-install-dismissed', until.toString());
    // P-194 (SPEC-128): NO resetear isInstallable ni chromeReportsInstalled.
    // Estos flags reflejan CAPACIDAD del navegador (beforeinstallprompt disparó
    // o Chrome reporta WebAPK), no estado UX del banner. Resetearlos hacía
    // que el InstallAppButton (P-173 — botón persistente SIN cooldown)
    // desapareciera tras cerrar el banner, porque InstallAppButton usa
    // `isInstallable` (que es `offerInstall = isInstallable || chromeReportsInstalled`).
    // El cooldown del banner se respeta vía el check de `pwa-install-dismissed`
    // en InstallBanner.useEffect + useInstallPrompt.useEffect — no necesita
    // resetear los flags de capacidad.
  }, []);

  return {
    isInstallable: offerInstall,
    isInstalled,
    webapkStale,
    install,
    dismiss,
  };
}
